import {
  BATTLE_STATE,
  COMBAT_EVENT_TYPE,
  IBattleAutoToggledData,
  IBattleCell,
  IBattleFighter,
  IBattleFinishedData,
  IBattleGridCell,
  IBattleRoundResolvedData,
  IBattleView,
  IEliminationEvent,
  IMoveEvent,
  MOVE_STATUS
} from "@/store/apis/battleAPI/battle.types"

interface IFieldState {
  fighters: IBattleFighter[]
  grid: IBattleGridCell[]
}

const clearFighterCells = (
  grid: IBattleGridCell[],
  fighterId: string
): IBattleGridCell[] =>
  grid.map((cell) =>
    cell.fighterId === fighterId ? { ...cell, fighterId: null } : cell
  )

const isSameCell = (a: IBattleCell, b: IBattleCell): boolean =>
  a.column === b.column && a.row === b.row

const moveFighter = (state: IFieldState, event: IMoveEvent): IFieldState => {
  const exists = state.fighters.some((f) => f.fighterId === event.characterId)
  if (event.status !== MOVE_STATUS.SUCCESS || !exists) {
    return state
  }
  return {
    fighters: state.fighters.map((fighter) =>
      fighter.fighterId === event.characterId
        ? { ...fighter, cell: event.to }
        : fighter
    ),
    grid: clearFighterCells(state.grid, event.characterId).map((cell) =>
      isSameCell(cell, event.to)
        ? { ...cell, fighterId: event.characterId }
        : cell
    )
  }
}

const eliminateFighter = (
  state: IFieldState,
  event: IEliminationEvent
): IFieldState => {
  if (!state.fighters.some((f) => f.fighterId === event.characterId)) {
    return state
  }
  return {
    fighters: state.fighters.map((fighter) =>
      fighter.fighterId === event.characterId
        ? { ...fighter, cell: null, alive: false }
        : fighter
    ),
    grid: clearFighterCells(state.grid, event.characterId)
  }
}

const applyFieldEvents = (
  state: IFieldState,
  events: IBattleRoundResolvedData["events"]
): IFieldState =>
  events.reduce((acc, event) => {
    switch (event.type) {
      case COMBAT_EVENT_TYPE.MOVE:
        return moveFighter(acc, event)
      case COMBAT_EVENT_TYPE.ELIMINATION:
        return eliminateFighter(acc, event)
      default:
        return acc
    }
  }, state)

export const applyRoundResolved = (
  view: IBattleView,
  data: IBattleRoundResolvedData
): IBattleView => {
  if (data.battleId !== view.id || data.round < view.round) {
    return view
  }
  const withHp = view.fighters.map((fighter) =>
    fighter.fighterId in data.hpSnapshot
      ? { ...fighter, hp: data.hpSnapshot[fighter.fighterId] }
      : fighter
  )
  const { fighters, grid } = applyFieldEvents(
    { fighters: withHp, grid: view.grid ?? [] },
    data.events
  )
  return {
    ...view,
    fighters,
    grid,
    lastRound: {
      round: data.round,
      resolvedBy: data.resolvedBy,
      events: data.events
    },
    round: data.nextRound ?? view.round,
    roundDeadline: data.nextDeadline
  }
}

export const applyAutoToggled = (
  view: IBattleView,
  data: IBattleAutoToggledData
): IBattleView =>
  data.battleId !== view.id
    ? view
    : { ...view, auto: data.auto, roundDeadline: data.nextDeadline }

export const applyBattleFinished = (
  view: IBattleView,
  data: IBattleFinishedData
): IBattleView =>
  data.battleId !== view.id
    ? view
    : {
        ...view,
        state: BATTLE_STATE.CLOSED,
        outcome: data.outcome,
        endReason: data.endReason,
        killId: data.killId,
        roundDeadline: null
      }
